import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { useDispatch, useSelector } from 'react-redux';
import { startOfMonth, endOfMonth } from 'date-fns';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { getRequiredUnderlineTextFieldSx } from '../base/requiredFieldUnderline';

import {
  changeCollectionsFilterStartBusinessDate,
  changeCollectionsFilterEndBusinessDate,
  reportCollectionsFilterStartBusinessDateSelector,
  reportCollectionsFilterEndBusinessDateSelector,
} from '../../state';

type MonthOnlyDateFilterProps = {
  requiredIncomplete?: boolean;
};

const MonthOnlyDateFilter = ({ requiredIncomplete }: MonthOnlyDateFilterProps) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const startDate = useSelector(reportCollectionsFilterStartBusinessDateSelector);
  const endDate = useSelector(reportCollectionsFilterEndBusinessDateSelector);

  const dateValue = useMemo(() => {
    if (startDate?.raw && endDate?.raw) {
      const start = startDate.raw as Date;
      const end = endDate.raw as Date;

      if (
        startOfMonth(start).getTime() === start.getTime() &&
        endOfMonth(start).getTime() === end.getTime()
      ) {
        return start;
      }
    }
    return null;
  }, [startDate?.raw, endDate?.raw]);

  const handleChange = useCallback(
    (newValue: Date | null) => {
      if (newValue) {
        const start = startOfMonth(newValue);
        const end = endOfMonth(newValue);

        dispatch(changeCollectionsFilterStartBusinessDate(start));
        dispatch(changeCollectionsFilterEndBusinessDate(end));
      }
    },
    [dispatch]
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <DatePicker
        label={t('toolbar.labels.monthReport')}
        views={['year', 'month']}
        minDate={new Date('2022-01-01')}
        maxDate={new Date()}
        value={dateValue}
        onChange={handleChange}
        slotProps={{
          textField: {
            variant: 'standard',
            size: 'small',
            fullWidth: true,
            sx: requiredIncomplete ? theme => getRequiredUnderlineTextFieldSx(theme) : undefined,
          },
        }}
      />
    </LocalizationProvider>
  );
};

export default MonthOnlyDateFilter;
